import { Countries } from '@/comm'
import { useDatePicker } from '@/components/MyDatePicker'
import { usePid2 } from '@/components/DropdownPid'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { getDateRange } from '@/lib/date-utils'

import { Button } from '@/components/ui/button'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'
import {
    useColumnFilters,
    VSWeekSelector,
} from '@/components/data-table-selectors'

import useSWR from 'swr'
import Td2 from '../../../components/zthd/Td2'
import { columnDate } from './columns'

const useColumnVisibilityStore = createColumnVisibilityStore({}, 'ccc')

const costsColumn = (code, name) => ({
    id: `${code}-costs`,
    header: `Costs`,
    accessorFn: (row) => {
        const curr = row.datas?.[code]
        const total = row.datas?.total
        return { curr, total }
    },
    cell: (row) => {
        const { curr, total } = row.getValue()
        const formatted = formatUSD(curr?.costs, 0, 0)
        const per = formatPercent(curr?.costs / total?.costs)
        return <Td2 line2={per}>{formatted}</Td2>
    },
})

const installsColumn = (code, name) => ({
    id: `${code}-installs`,
    header: 'Installs',
    accessorFn: (row) => {
        const curr = row.datas?.[code]
        const total = row.datas?.total
        return { curr, total }
    },
    cell: (row) => {
        const { curr, total } = row.getValue()
        const formatted = formatNumber(curr?.installs, 0, 0)
        const per = formatPercent(curr?.installs / total?.installs)
        return <Td2 line2={per}>{formatted}</Td2>
    },
})

const cpiColumn = (code, name) => ({
    id: `${code}-cpi`,
    header: 'CPI',
    accessorFn: (row) => {
        const curr = row.datas?.[code]
        return curr?.costs / curr?.installs
    },
    cell: (row) => {
        const cpi = row.getValue()
        const formatted = formatUSD(cpi, 4, 4)
        return <Td2 line2="-">{formatted}</Td2>
    },
})

const Home = () => {
    const startDate = useDatePicker((state) => state.startDate)
    const endDate = useDatePicker((state) => state.endDate)
    const pids = usePid2((state) => state.pids)

    const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
    const columnFilters = useColumnFilters((state) => state.columnFilters)

    const { data: costs, mutate } = useSWR([
        '/costs/countrycontrast',
        { startDate, endDate, pid: pids.join(',') },
    ])

    const data = useMemo(() => {
        const dateRange = getDateRange(dayjs(startDate), dayjs(endDate))

        return dateRange.map((date) => {
            const originalData = costs?.[date]

            if (!originalData) {
                return {
                    date,
                    datas: originalData,
                }
            }

            // Process the data and calculate totals
            const processedData = { ...originalData }

            // Calculate total costs and installs
            const totalCosts = Object.values(originalData).reduce((sum, country) => {
                return sum + (country?.costs || 0)
            }, 0)

            const totalInstalls = Object.values(originalData).reduce((sum, country) => {
                return sum + (country?.installs || 0)
            }, 0)

            processedData.total = {
                costs: totalCosts,
                installs: totalInstalls
            }

            return {
                date,
                datas: processedData,
            }
        })
    }, [startDate, endDate, costs])

    const columns = useMemo(() => {
        const countries = Countries.filter(({ code }) => code !== 'total')
        const rows = []
        rows.push(columnDate)

        rows.push({
            id: 'total',
            header: 'Total',
            columns: [
                costsColumn('total', 'Costs'),
                installsColumn('total', 'Installs'),
                cpiColumn('total', 'CPI'),
            ],
        })

        countries.forEach((country) => {
            const { code, name } = country
            rows.push({
                id: `${name}`,
                header: `${name}`,
                columns: [
                    costsColumn(code, name),
                    installsColumn(code, name),
                    cpiColumn(code, name),
                ],
            })
        })
        return rows
    }, [])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        enableMultiRowSelection: true,
        state: {
            columnFilters,
            columnVisibility,
        },
    })

    return (
        <>
            <div className="flex items-center gap-1 py-4">
                <VSWeekSelector />
                <Button onClick={mutate}>refresh</Button>
                <DataTableViewOptions table={table} />
            </div>
            <DataTableWithBorder table={table} />
        </>
    )
}

export default Home 